import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { supabase as cloudSupabase } from './client';
import {
  EXTERNAL_SUPABASE_URL,
  EXTERNAL_SUPABASE_ANON_KEY,
} from '@/config/externalSupabase';

/**
 * Driver-specific Supabase client.
 * 
 * Unlike the general external-client (which routes auth to Cloud),
 * this client routes EVERYTHING to the external Supabase project
 * EXCEPT `functions` which stays on Lovable Cloud.
 * 
 * This ensures auth.uid() resolves correctly on the external DB
 * for RLS policies, Realtime subscriptions, and direct queries.
 */
const externalDriverClient = createClient<Database>(
  EXTERNAL_SUPABASE_URL,
  EXTERNAL_SUPABASE_ANON_KEY,
  {
    auth: {
      storage: typeof window !== 'undefined' ? window.localStorage : undefined,
      persistSession: true,
      autoRefreshToken: true,
      storageKey: 'driver-auth-token',
    },
  }
);

/**
 * Driver client export:
 * - `.auth`, `.from()`, `.storage`, `.channel()`, `.removeChannel()`, `.rpc()` → external DB
 * - `.functions` → Lovable Cloud (edge functions are hosted there)
 */
export const driverSupabase = new Proxy(externalDriverClient, {
  get(target, prop, receiver) {
    // Only route functions to Cloud — everything else stays on external
    if (prop === 'functions') {
      return (cloudSupabase as any)[prop];
    }
    return Reflect.get(target, prop, receiver);
  },
}) as SupabaseClient<Database>;
